import React from 'react';

interface UniversityListItem {
  id: number;
  name: string;
  location: string;
  acceptance_rate: number;
  sat_range_min?: number;
  sat_range_max?: number;
}

interface UniversitySidebarProps {
  universities: UniversityListItem[];
  selectedUniversityId: number | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onUniversitySelect: (universityId: number) => void;
  universitiesLoading: boolean;
  totalUniversities: number;
  hasNextPage: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

const UniversitySidebar: React.FC<UniversitySidebarProps> = ({
  universities,
  selectedUniversityId,
  searchQuery,
  onSearchChange,
  onUniversitySelect,
  universitiesLoading,
  totalUniversities,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
}) => {
  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-screen">
      <div className="p-6 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">University Admin</h1>
        <p className="text-sm text-gray-500 mt-1">Manage university data</p>
      </div>
      
      <div className="p-4 border-b border-gray-100">
        <input
          type="text"
          placeholder="Search universities..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: '8rem' }}>
        <div className="p-2">
          {universitiesLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Loading universities...</p>
            </div>
          ) : (
            universities.map((uni) => (
              <div
                key={uni.id}
                onClick={() => onUniversitySelect(uni.id)}
                className={`p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2 ${
                  selectedUniversityId === uni.id
                    ? 'border-l-4 border-blue-500 bg-blue-50'
                    : ''
                }`}
              >
                <div className={`font-semibold text-lg mb-2 ${
                  selectedUniversityId === uni.id ? 'text-blue-700' : 'text-gray-900'
                }`}>
                  {uni.name}
                </div>
                <div className="text-sm text-gray-600 mb-1">{uni.location}</div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{uni.acceptance_rate}% acceptance</span>
                  <span>
                    {uni.sat_range_min && uni.sat_range_max 
                      ? `SAT: ${uni.sat_range_min}`
                      : uni.sat_range_min 
                        ? `SAT: ${uni.sat_range_min}`
                        : 'SAT: N/A'
                    }
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {!universitiesLoading && universities.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <div className="text-sm text-gray-600 mb-4 text-center">
              Showing {universities.length} of {totalUniversities} universities
            </div>
          </div>
        )}

        {hasNextPage && !universitiesLoading && (
          <div className="mt-4 text-center">
            <button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2 mx-auto"
            >
              {isLoadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <span>Load More</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversitySidebar;